import {
  SpaceBetween,
  FormField,
  Input,
  Textarea,
  SegmentedControl,
  Autosuggest,
  SegmentedControlProps,
  AutosuggestProps,
  Select,
  SelectProps,
  Button,
} from '@amzn/awsui-components-react';
import { useState, useRef, useEffect } from 'react';
import { OptionalLabel } from './Common';
import { getEnabledFeatures, useFeatures } from '../../../../hooks/features';
import { loadLocationSuggestions } from '../../../../hooks/locations';
import { autocomplete, getPlace, geocode } from '../../../../clients/amazonLocationClient';
import { useSearchParams } from 'react-router-dom';
import { useTimezone } from '../../../../hooks/timezone';
import { EventInput, LocationType } from '@amzn/red-velvet-api';
import { PapiLocation } from '@amzn/red-velvet-api';
import { publishKatalMetric } from '../../../katalAnalytics';
import { FIELD_LIMITS, getCharLimitProps } from '../../../../utils/fieldConstraints';
import { useLocalizedText } from '../../../../hooks/localizedText';
import { useTOptions } from '../../../../hooks/localization/tOptions';
import { TIMEZONES } from '../../../../data/eventOptionLists';
import { getLocationAddressLines } from '../../../../utils/locationFormatting';

export interface LocationComponentProps {
  currentFields: EventInput;
  updateFieldHandler: (fields: EventInput) => void;
  errors?: Record<string, string>;
  isReadOnly?: boolean;
  metricPrefix: string;
}

const LOCATION_AUTO_COMPLETE_MIN_CHAR = 2;

export function LocationComponent(props: LocationComponentProps) {
  const namespaces = ['events', 'translation'];
  const { t } = useLocalizedText(namespaces);
  const locationTypeOption: Record<LocationType, SegmentedControlProps.Option> = {
    ON_SITE: {
      text: t('eventDetailsStep.onCampus'),
      id: LocationType.ON_SITE,
      disabled: props.isReadOnly,
    },
    OFF_SITE: {
      text: t('eventDetailsStep.offCampus'),
      id: LocationType.OFF_SITE,
      disabled: props.isReadOnly,
    },
    VIRTUAL: {
      text: t('eventDetailsStep.virtual'),
      id: LocationType.VIRTUAL,
      disabled: props.isReadOnly,
    },
  };

  const initialType = props.currentFields.location?.locationType ?? LocationType.ON_SITE;
  const [locationType, setLocationType] = useState(locationTypeOption[initialType]);
  // Cache full location object per type so we can restore geo fields on type switch
  const locationCache = useRef<Partial<Record<LocationType, EventInput['location']>>>({
    [initialType]: props.currentFields.location,
  });
  const [locationVirtualNotes, setLocationVirtualNotes] = useState(
    props.currentFields.locationVirtualNotes ?? ''
  );
  const [locationNotes, setLocationNotes] = useState(
    initialType === LocationType.ON_SITE ? (props.currentFields.location?.notes ?? '') : ''
  );
  const [offCampusNotes, setOffCampusNotes] = useState(
    initialType === LocationType.OFF_SITE ? (props.currentFields.location?.notes ?? '') : ''
  );
  // Additional Fields for Off campus location — only init from props if type is OFF_CAMPUS
  const [location, setLocation] = useState(
    initialType === LocationType.OFF_SITE
      ? (props.currentFields.location?.address_string ?? '')
      : ''
  );
  const [locationName, setLocationName] = useState(
    initialType === LocationType.OFF_SITE ? (props.currentFields.location?.name ?? '') : ''
  );
  // Additional Fields for On campus location — only init from props if type is ON_CAMPUS
  const [building, setBuilding] = useState(
    initialType === LocationType.ON_SITE ? props.currentFields.location?.amazonBuilding : undefined
  );
  const [room, setRoom] = useState(
    initialType === LocationType.ON_SITE
      ? (props.currentFields.location?.amazonBuildingRoom ?? '')
      : ''
  );
  // Timezone for virtual events — grouped list (region headers + zone labels with baked UTC offsets)
  // from ARB or the legacy i18next source, chosen by the Localization flag; useTOptions preserves the
  // two-level grouping so the Select renders the same shape either way.
  const timezoneOptions = useTOptions(namespaces, TIMEZONES);
  const userTimezone = useTimezone();
  const findTimezoneOption = (tz: string): SelectProps.Option | null => {
    for (const group of timezoneOptions) {
      const match = (group as SelectProps.OptionGroup).options?.find((o) => o.value === tz);
      if (match) return match;
    }
    return null;
  };
  // Store the selected zone as its value code, not the resolved option object: on the first render
  // with the Localization flag on, the ARB pack is still loading and useTOptions returns the English
  // legacy list, so freezing the option here would pin its English label in the closed Select even
  // after the translated list arrives. The value code is language-independent, so seed it and derive
  // the option each render, keeping the trigger label in sync with the list.
  const [timezoneValue, setTimezoneValue] = useState<string | null>(() => {
    const saved = props.currentFields.timeZone ?? userTimezone;
    return saved && findTimezoneOption(saved) ? saved : null;
  });
  const timezone = timezoneValue ? findTimezoneOption(timezoneValue) : null;

  const [options, setOptions] = useState<AutosuggestProps.Options>([]);
  const [status, setStatus] = useState<AutosuggestProps.StatusType>();
  const [searchParams] = useSearchParams();
  const features = useFeatures();

  const fieldsRef = useRef(props.currentFields);
  fieldsRef.current = props.currentFields;

  const placeIdMap = useRef(new Map<string, string>());
  const papiLocationMap = useRef(new Map<string, PapiLocation>());

  // cce-8161: inline editor that replaces an on-campus building's geocoded address (PAPI's address
  // can be wrong) while keeping the building, room and ON_SITE type.
  const [isEditingAddress, setIsEditingAddress] = useState(false);
  const [addressQuery, setAddressQuery] = useState('');
  const [pendingPlace, setPendingPlace] = useState<{ placeId: string; label: string }>();
  const [isSavingAddress, setIsSavingAddress] = useState(false);
  const [addressSaveError, setAddressSaveError] = useState<string>();

  // Writes only the address, coordinate and timezone fields, so the other location fields (for an
  // on-campus event: amazonBuilding, amazonBuildingRoom and the ON_SITE type) are kept. Shared by
  // the Off campus search and the on-campus address override. Returns whether the address applied.
  const resolvePlaceAddress = async (placeId: string, label: string): Promise<boolean> => {
    try {
      const response = await getPlace({
        PlaceId: placeId,
        IntendedUse: 'Storage',
        AdditionalFeatures: ['TimeZone'],
      });
      if (!response.Position) return false;
      props.updateFieldHandler({
        ...fieldsRef.current,
        location: {
          ...fieldsRef.current.location,
          latitude: response.Position[1],
          longitude: response.Position[0],
          address_string: response.Address?.Label ?? response.Title ?? label,
          // House number lives in AddressNumber, not Street.
          street:
            [response.Address?.AddressNumber, response.Address?.Street].filter(Boolean).join(' ') ||
            undefined,
          city: response.Address?.Locality,
          province: response.Address?.Region?.Name,
          country: response.Address?.Country?.Name,
          postal_code: response.Address?.PostalCode,
        },
        ...(response.TimeZone?.Name && { timeZone: response.TimeZone.Name }),
      });
      return true;
    } catch (e) {
      console.error('Failed to resolve place:', e);
      return false;
    }
  };

  const closeAddressEditor = () => {
    setIsEditingAddress(false);
    setAddressQuery('');
    setPendingPlace(undefined);
    setAddressSaveError(undefined);
  };

  const saveBuildingAddress = async () => {
    if (!pendingPlace) return;
    setIsSavingAddress(true);
    const saved = await resolvePlaceAddress(pendingPlace.placeId, pendingPlace.label);
    setIsSavingAddress(false);
    if (!saved) {
      setAddressSaveError(t('eventDetailsStep.buildingAddressSaveError'));
      return;
    }
    publishKatalMetric(`${props.metricPrefix}_buildingAddressOverridden`, building ?? '');
    closeAddressEditor();
  };

  const resolveOnCampusBuilding = async (papiLocation: PapiLocation) => {
    try {
      const addr = papiLocation.address;
      const queryText = [addr.unifiedAddressLine, addr.city, addr.region, addr.country]
        .filter(Boolean)
        .join(', ');
      const response = await geocode({
        QueryText: queryText,
        IntendedUse: 'Storage',
        AdditionalFeatures: ['TimeZone'],
      });
      console.log('On-campus geocode response:', response);
      const result = response.ResultItems?.[0];
      if (result?.Position) {
        props.updateFieldHandler({
          ...fieldsRef.current,
          location: {
            ...fieldsRef.current.location,
            latitude: result.Position[1],
            longitude: result.Position[0],
            address_string: result.Address?.Label ?? result.Title ?? queryText,
            // House number lives in AddressNumber, not Street.
            street:
              [result.Address?.AddressNumber, result.Address?.Street].filter(Boolean).join(' ') ||
              undefined,
            city: result.Address?.Locality,
            province: result.Address?.Region?.Name,
            country: result.Address?.Country?.Name,
            postal_code: result.Address?.PostalCode,
          },
          ...(result.TimeZone?.Name && { timeZone: result.TimeZone.Name }),
        });
      }
    } catch (e) {
      console.error('Failed to geocode on-campus building:', e);
    }
  };

  // VOLUNTEERCENTER-777: a prefilled building_code (from a deep link) seeds
  // location.amazonBuilding without going through the building autosuggest, so the geocode
  // enrichment resolveOnCampusBuilding normally performs — latitude/longitude/city/country/
  // timezone — was skipped. Run it once on mount, but ONLY for that case: an on-site building
  // with no coordinates yet. Resumed drafts already carry coordinates and are left untouched.
  // If PAPI has no exact match for the code, the bare building string stays as-is (current behaviour).
  const prefillBuildingResolved = useRef(false);
  useEffect(() => {
    if (prefillBuildingResolved.current) return;
    const loc = props.currentFields.location;
    if (
      loc?.locationType === LocationType.ON_SITE &&
      loc.amazonBuilding &&
      loc.latitude === undefined
    ) {
      prefillBuildingResolved.current = true;
      const code = loc.amazonBuilding;
      (async () => {
        try {
          const { papiLocations } = await loadLocationSuggestions(
            code,
            'on-campus',
            getEnabledFeatures(features, searchParams)
          );
          // papiLocations is keyed by PAPI's canonical building value, while the code comes from a
          // hand-written deep link, so compare case- and whitespace-insensitively (sea41 → SEA41).
          // No fallback to the first result: an on-campus search can return several buildings.
          const normalized = code.trim().toUpperCase();
          const match =
            papiLocations.get(code) ??
            Array.from(papiLocations.entries()).find(
              ([key]) => key.trim().toUpperCase() === normalized
            )?.[1];
          if (match) resolveOnCampusBuilding(match);
        } catch (e) {
          console.error('Failed to resolve prefilled building code:', e);
        }
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run once on mount for deep-link prefill
  }, []);

  const loadLocationOptions = async (
    filteringText: string,
    eventType: 'on-campus' | 'off-campus' | 'virtual'
  ) => {
    // Location autocomplete api only loads with 2+ characters
    if (filteringText.length >= LOCATION_AUTO_COMPLETE_MIN_CHAR) {
      try {
        setStatus('loading');
        setOptions([]);

        let locationOptions: AutosuggestProps.Option[];
        if (eventType === 'on-campus') {
          // On-campus: use PAPI for Amazon building code lookup
          const result = await loadLocationSuggestions(
            filteringText,
            eventType,
            getEnabledFeatures(features, searchParams)
          );
          locationOptions = result.options;
          papiLocationMap.current = result.papiLocations;
        } else {
          // Off-campus: use Amazon Location Service autocomplete
          const response = await autocomplete({
            QueryText: filteringText,
            MaxResults: 10,
          });
          placeIdMap.current.clear();
          locationOptions = (response.ResultItems ?? []).map((item) => {
            const value = item.Title ?? '';
            if (item.PlaceId) placeIdMap.current.set(value, item.PlaceId);
            return { value, label: value, description: item.PlaceType };
          });
        }

        setOptions(locationOptions);
        setStatus('finished');
      } catch (e) {
        setStatus('error');
      }
    } else {
      setOptions([]);
    }
  };

  const renderOnCampusQuestion = () => {
    const buildingAddressLines = getLocationAddressLines(props.currentFields.location);
    const roomCharProps = getCharLimitProps(
      room ?? '',
      FIELD_LIMITS.ROOM,
      t,
      'eventDetailsStep.roomConstraint'
    );
    const notesCharProps = getCharLimitProps(
      locationNotes,
      FIELD_LIMITS.LOCATION_NOTES,
      t,
      'eventDetailsStep.locationNotesConstraint'
    );
    return (
      <SpaceBetween direction="vertical" size="l">
        <FormField
          label={t('eventDetailsStep.building')}
          description={t('eventDetailsStep.buildingSubText')}
          errorText={props.errors?.building}
        >
          <SpaceBetween direction="vertical" size="xs">
            <Select
              selectedOption={building ? { value: building, label: building } : null}
              onChange={(event) => {
                const selected = event.detail.selectedOption;
                // A new building gets a fresh geocode below, which replaces any address override.
                closeAddressEditor();
                setBuilding(selected.value);
                props.updateFieldHandler({
                  ...props.currentFields,
                  location: {
                    ...props.currentFields.location,
                    amazonBuilding: selected.value,
                    amazonBuildingRoom: room,
                    name: room?.trim() ? `${selected.value} - ${room}` : selected.value,
                    notes: locationNotes,
                    // Drop the previous building's geocoded fields so a slow or failed geocode below
                    // can't leave the new building code paired with the old building's address.
                    address_string: undefined,
                    street: undefined,
                    city: undefined,
                    province: undefined,
                    country: undefined,
                    postal_code: undefined,
                    latitude: undefined,
                    longitude: undefined,
                  },
                });
                const papiLocation = papiLocationMap.current.get(selected.value ?? '');
                if (papiLocation) {
                  resolveOnCampusBuilding(papiLocation);
                }
              }}
              placeholder={t('eventDetailsStep.buildingPlaceholder')}
              statusType={status}
              options={options}
              empty={t('additionalInfo.noLocationSuggestions')}
              loadingText={t('additionalInfo.loadingLocationSuggestions')}
              onLoadItems={(event) => {
                loadLocationOptions(event.detail.filteringText, 'on-campus');
              }}
              filteringType="manual"
              virtualScroll
              readOnly={props.isReadOnly}
            />
            {/* cce-8161: show the selected building's resolved address, formatted as the event detail
              page shows it, so a coordinator can spot a wrong PAPI address and correct it. */}
            {building && !isEditingAddress && (
              <div data-testid="selected-building-address">
                {buildingAddressLines.map((line) => (
                  <div key={line}>{line}</div>
                ))}
                <div>{t('eventDetailsStep.buildingAddressHint')}</div>
                {!props.isReadOnly && (
                  <Button
                    variant="inline-link"
                    onClick={() => setIsEditingAddress(true)}
                    data-testid="edit-building-address"
                  >
                    {t('eventDetailsStep.editBuildingAddress')}
                  </Button>
                )}
              </div>
            )}
          </SpaceBetween>
        </FormField>
        {building && isEditingAddress && (
          <FormField
            label={t('eventDetailsStep.buildingAddressEditLabel')}
            errorText={addressSaveError}
          >
            <SpaceBetween direction="vertical" size="xs">
              {/* Same Amazon Location Service search the Off campus field uses. */}
              <Autosuggest
                filteringType="manual"
                value={addressQuery}
                onChange={({ detail }) => {
                  setAddressQuery(detail.value);
                  setPendingPlace(undefined);
                  setAddressSaveError(undefined);
                }}
                onSelect={({ detail }) => {
                  const placeId = placeIdMap.current.get(detail.value);
                  setPendingPlace(placeId ? { placeId, label: detail.value } : undefined);
                }}
                placeholder={t('eventDetailsStep.locationPlaceholder')}
                statusType={status}
                options={options}
                empty={t('additionalInfo.noLocationSuggestions')}
                loadingText={t('additionalInfo.loadingLocationSuggestions')}
                onLoadItems={({ detail }) => {
                  loadLocationOptions(detail.filteringText, 'off-campus');
                }}
                virtualScroll
                data-testid="building-address-search"
              />
              <SpaceBetween direction="horizontal" size="xs">
                <Button onClick={closeAddressEditor} data-testid="cancel-building-address">
                  {t('eventDetailsStep.cancelBuildingAddress')}
                </Button>
                <Button
                  variant="primary"
                  disabled={!pendingPlace}
                  loading={isSavingAddress}
                  onClick={saveBuildingAddress}
                  data-testid="save-building-address"
                >
                  {t('eventDetailsStep.saveBuildingAddress')}
                </Button>
              </SpaceBetween>
            </SpaceBetween>
          </FormField>
        )}
        <FormField
          label={t('eventDetailsStep.room')}
          description={t('eventDetailsStep.roomSubText')}
          constraintText={roomCharProps.constraintText}
          errorText={props.errors?.room ?? roomCharProps.errorText}
        >
          <Input
            placeholder={t('eventDetailsStep.roomPlaceholder')}
            onChange={(event) => setRoom(event.detail.value)}
            onBlur={() => {
              props.updateFieldHandler({
                ...props.currentFields,
                location: {
                  ...props.currentFields.location,
                  name: building?.trim() ? `${building} - ${room}` : room,
                  amazonBuilding: building,
                  amazonBuildingRoom: room,
                  notes: locationNotes,
                },
              });
            }}
            value={room}
            readOnly={props.isReadOnly}
          />
        </FormField>
        <FormField
          label={
            <SpaceBetween direction="horizontal" size="l">
              <OptionalLabel>{t('eventDetailsStep.locationVirtualNotes')}</OptionalLabel>
            </SpaceBetween>
          }
          description={t('eventDetailsStep.locationVirtualNotesSubText')}
          constraintText={notesCharProps.constraintText}
          errorText={props.errors?.locationNotes ?? notesCharProps.errorText}
        >
          <Textarea
            onChange={(event) => setLocationNotes(event.detail.value)}
            onBlur={() => {
              props.updateFieldHandler({
                ...props.currentFields,
                location: {
                  ...props.currentFields.location,
                  name: building?.trim() ? `${building} - ${room}` : room,
                  amazonBuilding: building,
                  amazonBuildingRoom: room,
                  notes: locationNotes,
                },
              });
            }}
            value={locationNotes}
            placeholder={t('eventDetailsStep.locationVirtualNotesPlaceholder')}
            readOnly={props.isReadOnly}
          />
        </FormField>
      </SpaceBetween>
    );
  };

  const renderOffCampusQuestion = () => {
    const nameCharProps = getCharLimitProps(
      locationName,
      FIELD_LIMITS.LOCATION_NAME,
      t,
      'eventDetailsStep.locationNameConstraint'
    );
    const notesCharProps = getCharLimitProps(
      offCampusNotes,
      FIELD_LIMITS.LOCATION_NOTES,
      t,
      'eventDetailsStep.locationNotesConstraint'
    );
    return (
      <SpaceBetween direction="vertical" size="l">
        <FormField
          label={t('eventDetailsStep.location')}
          description={t('eventDetailsStep.locationSubText')}
          errorText={props.errors?.location}
        >
          <Autosuggest
            filteringType="manual"
            value={location}
            onChange={(event) => {
              setLocation(event.detail.value || '');
            }}
            onSelect={({ detail }) => {
              const placeId = placeIdMap.current.get(detail.value);
              if (placeId) {
                resolvePlaceAddress(placeId, detail.value);
              }
            }}
            placeholder={t('eventDetailsStep.locationPlaceholder')}
            statusType={status}
            options={options}
            empty={t('additionalInfo.noLocationSuggestions')}
            loadingText={t('additionalInfo.loadingLocationSuggestions')}
            onLoadItems={(event) => {
              loadLocationOptions(event.detail.filteringText, 'off-campus');
            }}
            virtualScroll
            readOnly={props.isReadOnly}
          />
        </FormField>
        <FormField
          label={t('eventDetailsStep.locationName')}
          description={t('eventDetailsStep.locationNameSubText')}
          constraintText={nameCharProps.constraintText}
          errorText={props.errors?.locationName ?? nameCharProps.errorText}
        >
          <Input
            value={locationName}
            placeholder={t('eventDetailsStep.locationNamePlaceholder')}
            readOnly={props.isReadOnly}
            onChange={(event) => setLocationName(event.detail.value)}
            onBlur={() => {
              props.updateFieldHandler({
                ...props.currentFields,
                location: {
                  ...props.currentFields.location,
                  name: locationName,
                  address_string: location,
                  notes: offCampusNotes,
                },
              });
            }}
          />
        </FormField>
        <FormField
          label={
            <SpaceBetween direction="horizontal" size="l">
              <OptionalLabel>{t('eventDetailsStep.locationVirtualNotes')}</OptionalLabel>
            </SpaceBetween>
          }
          description={t('eventDetailsStep.locationVirtualNotesSubText')}
          constraintText={notesCharProps.constraintText}
          errorText={props.errors?.locationNotes ?? notesCharProps.errorText}
        >
          <Textarea
            onChange={(event) => setOffCampusNotes(event.detail.value)}
            onBlur={() => {
              props.updateFieldHandler({
                ...props.currentFields,
                location: {
                  ...props.currentFields.location,
                  name: locationName,
                  address_string: location,
                  notes: offCampusNotes,
                },
              });
            }}
            value={offCampusNotes}
            placeholder={t('eventDetailsStep.locationVirtualNotesPlaceholder')}
            readOnly={props.isReadOnly}
          />
        </FormField>
      </SpaceBetween>
    );
  };

  const renderVirtualQuestion = () => {
    const virtualNotesCharProps = getCharLimitProps(
      locationVirtualNotes,
      FIELD_LIMITS.LOCATION_NOTES,
      t,
      'eventDetailsStep.virtualNotesConstraint'
    );
    return (
      <SpaceBetween direction="vertical" size="l">
        <FormField
          label={t('eventDetailsStep.eventTimezone')}
          description={t('eventDetailsStep.eventTimezoneSubText')}
          errorText={props.errors?.timeZone}
        >
          <Select
            selectedOption={timezone}
            onChange={(event) => {
              setTimezoneValue(event.detail.selectedOption.value ?? null);
              props.updateFieldHandler({
                ...props.currentFields,
                timeZone: event.detail.selectedOption.value,
              });
            }}
            options={timezoneOptions}
            placeholder={t('eventDetailsStep.eventTimezonePlaceholder')}
            filteringType="auto"
            readOnly={props.isReadOnly}
          />
        </FormField>
        <FormField
          label={<OptionalLabel>{t('eventDetailsStep.virtualNotes')}</OptionalLabel>}
          description={t('eventDetailsStep.locationVirtualNotesSubText')}
          constraintText={virtualNotesCharProps.constraintText}
          errorText={props.errors?.locationVirtualNotes ?? virtualNotesCharProps.errorText}
        >
          <Textarea
            onChange={(event) => setLocationVirtualNotes(event.detail.value)}
            onBlur={() => {
              props.updateFieldHandler({
                ...props.currentFields,
                locationVirtualNotes: locationVirtualNotes,
              });
            }}
            value={locationVirtualNotes}
            placeholder={t('eventDetailsStep.virtualNotesPlaceholder')}
            readOnly={props.isReadOnly}
          />
        </FormField>
      </SpaceBetween>
    );
  };

  const renderAdditionalQuestion = () => {
    switch (locationType.id) {
      case LocationType.ON_SITE:
        return renderOnCampusQuestion();
      case LocationType.OFF_SITE:
        return renderOffCampusQuestion();
      case LocationType.VIRTUAL:
        return renderVirtualQuestion();
      default:
        return;
    }
  };

  return (
    <SpaceBetween direction="vertical" size="l">
      <FormField
        label={t('eventDetailsStep.locationType')}
        description={t('eventDetailsStep.locationTypeSubText')}
      >
        <SegmentedControl
          selectedId={locationType.id}
          onChange={(event) => {
            const selectedType = event.detail.selectedId as LocationType;
            publishKatalMetric(`${props.metricPrefix}_locationTypeSelected`, selectedType);
            const currentType = locationType.id as LocationType;
            setLocationType(locationTypeOption[event.detail.selectedId as LocationType]);
            const defaultTz = findTimezoneOption(userTimezone);
            if (selectedType === LocationType.VIRTUAL && !timezoneValue && defaultTz) {
              setTimezoneValue(defaultTz.value ?? null);
            } else if (
              selectedType !== LocationType.VIRTUAL &&
              currentType === LocationType.VIRTUAL
            ) {
              setTimezoneValue(null);
            }
            // Cache current type's location before switching
            locationCache.current[currentType] = props.currentFields.location;
            // Restore cached location for target type, with local state overrides
            const cached = locationCache.current[selectedType];
            let restoredLocation: EventInput['location'] | undefined;
            if (selectedType === LocationType.ON_SITE && building) {
              restoredLocation = {
                ...cached,
                amazonBuilding: building,
                amazonBuildingRoom: room,
                name: building?.trim() ? `${building} - ${room}` : room,
                notes: locationNotes,
              };
            } else if (selectedType === LocationType.OFF_SITE && location) {
              restoredLocation = {
                ...cached,
                address_string: location,
                name: locationName,
                notes: offCampusNotes,
              };
            }
            props.updateFieldHandler({
              ...props.currentFields,
              location: { ...restoredLocation, locationType: selectedType },
              locationVirtualNotes:
                selectedType === LocationType.VIRTUAL ? locationVirtualNotes : '',
              timeZone:
                selectedType === LocationType.VIRTUAL
                  ? (props.currentFields.timeZone ?? userTimezone)
                  : userTimezone,
            });
          }}
          options={[
            locationTypeOption[LocationType.ON_SITE],
            locationTypeOption[LocationType.OFF_SITE],
            locationTypeOption[LocationType.VIRTUAL],
          ]}
        />
      </FormField>
      {renderAdditionalQuestion()}
    </SpaceBetween>
  );
}
